import React, { useContext } from "react";

import { Button } from "@components/buttons";
import { GROUPED_BETTING_TYPES } from "@data-types/offer/event-betting-type/BettingTypeGroups";
import { TStatisticsBettingData } from "@data-types/offer/event-betting-type/StatisticsBettingData";
import { TStatisticsBettingDataSetterFn } from "@data-types/offer/event-betting-type/StatisticsBettingDataSetterFn";
import { TWO_COLUMNS_BETTING_TYPES } from "@data-types/offer/shared/TwoColumnsBettingTypes";
import TipButton from "@features/offer/mobile/shared/common/offer-grid/tip-button/TipButton";
import { OfferTips } from "@features/offer/mobile/shared/event/components/markets/OfferTips";
import { EventDetails } from "@features/offer/mobile/shared/offer-row/event-details/EventDetails";
import EventOutrightOffer from "@features/offer/mobile/shared/offer-row/event-details/event-outright-offer/EventOutrightOffer";
import { BettingTypeStatistics } from "@features/offer/mobile/statistics";
import { EventContext, LookupContext } from "@gp/components";
import { EventKeyBettingOffer, EventType, SpecifierType } from "@gp/models";
import { OfferConfigurationStore, constants } from "@gp/offer";
import { ISpecifiers, NameProvider, Sorter } from "@gp/utility";
import { OfferStore } from "@state/context/offer/OfferContext";
import useMapOfferIntoTipsForRender from "@state/hooks/offer/useMapOfferIntoTipsForRender";
import { useOfferContext } from "@state/hooks/offer/useOfferContext";
import { useOfferOptionsContext } from "@state/hooks/offer/useOfferOptionsContext";
import classNames from "classnames";
import { observer } from "mobx-react";

import BettingTypeStatisticsButton from "../../../button/BettingTypeStatisticsButton";
import { BettingTypeDescription } from "./BettingTypeDescription";
import { BettingTypeName } from "./BettingTypeName";
import { PlayerTipButton } from "./PlayerTipButton";
import { filterOffer } from "./filterOffer";

const offerConfigurationStore = new OfferConfigurationStore("european");
const TypeConfiguration = offerConfigurationStore.typeConfiguration;

const nameProvider = new NameProvider();

export default observer(function BettingTypeRow(props: {
	bettingType: { id: string } & {
		abrv: string;
		specifierType: SpecifierType;
	} & {
		nameForOfferList: string;
		description: string;
		abrv: string;
	};
	offerKey: { id: string } & {
		specifier?: { [key: string]: string };
	};
	isGroup: boolean;
	index: number;
	toggleState: TStatisticsBettingData | null;
	setToggleState: TStatisticsBettingDataSetterFn;
}) {
	const { bettingType, offerKey, isGroup = false } = props;

	//#region context

	const event = useContext(EventContext);
	const { teams, players } = useContext(LookupContext);
	const store = useOfferContext<OfferStore.EventViewStore>();
	const OfferOptionContext = useOfferOptionsContext();

	//#endregion context

	const keyOffers = store.keyOffersMap.get(offerKey.id);
	let offers: EventKeyBettingOffer[] = [];

	if (keyOffers != null) {
		offers = Array.from(keyOffers.values());
	}

	const homeTeam = teams.get(event.teamOneId || "");
	const awayTeam = teams.get(event.teamTwoId || "");

	const offerSpecifier = offers[0] ? offers[0].specifier : {};
	const specifiers: ISpecifiers = {
		...offerKey.specifier,
		...offerSpecifier,
		competitor1: homeTeam?.name || "",
		competitor2: awayTeam?.name || "",
	};

	const name = nameProvider.getName(bettingType.nameForOfferList, specifiers);

	const { isValidBettingOffer, isOfferHistoryValid, filterOffersByTeam } =
		filterOffer();

	if (bettingType.specifierType === SpecifierType.PLAYER) {
		const sortTips = (a: EventKeyBettingOffer, b: EventKeyBettingOffer) => {
			// @ts-expect-error is this really number?
			const aVal = parseFloat(a.value);
			// @ts-expect-error is this really number?
			const bVal = parseFloat(b.value);

			if (isNaN(aVal) || isNaN(bVal)) {
				return 0;
			}

			return Math.sign(aVal - bVal);
		};

		let filteringFunc = isValidBettingOffer;

		if (OfferOptionContext.isOfferHistory) {
			filteringFunc = isOfferHistoryValid;
		}

		const homeTeamOffers = filterOffersByTeam(
			event.teamOneId,
			offers,
			filteringFunc
		).sort(Sorter.sort(sortTips));

		const awayTeamOffers = filterOffersByTeam(
			event.teamTwoId,
			offers,
			filteringFunc
		).sort(Sorter.sort(sortTips));

		return (
			<React.Fragment>
				<div className="offer--additional__header">
					<BettingTypeName
						name={name}
						bettingType={bettingType}
						offerKey={offerKey}
						index={props.index}
						bettingTypeStats={props.toggleState}
						setBettingTypeStats={props.setToggleState}
						isFull
					/>

					<div className="offer--additional__actions">
						<BettingTypeStatisticsButton
							toggleState={props.toggleState}
							setToggleState={props.setToggleState}
							//@ts-expect-error
							bettingType={bettingType}
							offerKey={offerKey}
						/>

						<Button
							className={
								"btn--square btn--square--tny btn--ghost offer--additional__btn" /*  +
								(props.toggleState?.action == "info"
									? " btn--secondary"
									: " btn--ghost")
							*/
							}
							iconOnly={true}
							iconClassName={
								"u-icon--sml" +
								(props.toggleState?.action == "info"
									? " u-icon--info-circle--bold"
									: " u-icon--info-circle")
							}
							onClick={() => props.setToggleState("info", null)}
						/>
					</div>
				</div>

				<BettingTypeDescription
					description={nameProvider.getName(
						bettingType.description,
						specifiers
					)}
					shouldDisplay={props.toggleState?.action == "info"}
				/>

				{props.toggleState?.action != "stats" ? (
					<div className="offer--additional__market offer--player">
						<div className="offer--player__col">
							<div className="offer--player__subtitle">
								{homeTeam?.name}
							</div>
							<div className="offer--player__list offer--player__home">
								{homeTeamOffers.map((tip) => (
									<PlayerTipButton
										key={tip.id}
										bettingTypeAbrv={bettingType.abrv}
										players={players}
										toggleState={props.toggleState}
										tip={tip}
									/>
								))}
							</div>
						</div>
						<div className="offer--player__col">
							<div className="offer--player__subtitle">
								{awayTeam?.name}
							</div>
							<div className="offer--player__list offer--player__away">
								{awayTeamOffers.map((tip) => (
									<PlayerTipButton
										key={tip.id}
										bettingTypeAbrv={bettingType.abrv}
										players={players}
										toggleState={props.toggleState}
										tip={tip}
									/>
								))}
							</div>
						</div>
					</div>
				) : (
					<div className="offer--additional__stats">
						<div className="card card--raised card--players">
							<div className="card__header card--players__header">
								<div className="card__title card__title--sml u-type--wgt--regular">
									{homeTeam?.name}
								</div>
							</div>

							<div className="card__body card--players__body">
								{homeTeamOffers.map((tip) => (
									<div className="card--players__section">
										<PlayerTipButton
											key={tip.id}
											bettingTypeAbrv={bettingType.abrv}
											players={players}
											toggleState={props.toggleState}
											tip={tip}
											isStats
										/>
									</div>
								))}
							</div>
						</div>

						<div className="card card--raised card--players">
							<div className="card__header card--players__header">
								<div className="card__title card__title--sml u-type--wgt--regular">
									{awayTeam?.name}
								</div>
							</div>
							<div className="card__body card--players__body">
								{awayTeamOffers.map((tip) => (
									<div className="card--players__section">
										<PlayerTipButton
											key={tip.id}
											bettingTypeAbrv={bettingType.abrv}
											players={players}
											toggleState={props.toggleState}
											tip={tip}
											isStats
										/>
									</div>
								))}
							</div>
						</div>
					</div>
				)}
			</React.Fragment>
		);
	}

	const btConfig = new TypeConfiguration(
		bettingType.abrv,
		offers.map((o) => o.tip)
	);
	const tips = btConfig.tips.sorted;

	let tipsForRender = useMapOfferIntoTipsForRender(tips, offerKey.id);

	if (tipsForRender == null) {
		return null;
	}

	tipsForRender = tipsForRender.filter((t) => t.isMargin === false);

	if (OfferOptionContext.isOfferHistory) {
		tipsForRender = tipsForRender.filter((t) => t.tip?.status != null);
	}

	if (
		tipsForRender.filter((t) => t.tip !== null && t.tip?.tip).length ===
			1 &&
		tips.filter((tip) => tip !== constants.marginPlaceholder).length > 3
	) {
		tipsForRender.push({ isMargin: true, margin: "", key: `plchldr-1` });
	}

	if (event.eventType != null && event.eventType !== EventType.NORMAL) {
		const eventOffer = store.eventKeysMap.get(event.id);

		return (
			<div className="match--outright">
				<EventDetails shouldHideCollapseIcon />

				<EventOutrightOffer
					/* @ts-expect-error */
					eventOffer={eventOffer}
					offerStore={store}
					ignoreIsExpanded
				/>
			</div>
		);
	}

	if (bettingType.abrv === "winning-method") {
		return (
			<div
				className={classNames([
					`offer--additional__market offer--additional__market--${bettingType.abrv}`,
				])}
			>
				<div className="match__grid u-padd--xtny">
					<BettingTypeName
						name={name}
						index={props.index}
						bettingTypeStats={props.toggleState}
						setBettingTypeStats={props.setToggleState}
						bettingType={bettingType}
						offerKey={offerKey}
						isFull
					/>

					{tipsForRender.map((tip) => {
						if (tip == null || Object.keys(tip).length === 0) {
							/** If tip is null or is empty object. */
							return;
						}

						const tipKey =
							tip.tip != null
								? tip.tip.id
								: "margin" + tip.margin;
						/* @ts-expect-error */
						return <TipButton key={tipKey} isPlayer {...tip} />;
					})}
				</div>
			</div>
		);
	}

	if (GROUPED_BETTING_TYPES.includes(bettingType.abrv)) {
		const matchBlockClasses = classNames(
			[
				`offer--additional__market offer--additional__market--${bettingType.abrv}`,
			],
			{
				"offer--group__market": isGroup,
			}
		);

		return (
			<div className={matchBlockClasses}>
				<div className="offer--additional__subheader">
					<BettingTypeName
						name={
							//@ts-expect-error
							offerKey.specifier["hcp"] ||
							//@ts-expect-error
							offerKey.specifier["total"]
						}
						isGroup={isGroup}
						bettingType={bettingType}
						offerKey={offerKey}
						bettingTypeStats={props.toggleState}
						setBettingTypeStats={props.setToggleState}
						index={props.index}
					/>
				</div>
				<div
					className={classNames("offer--additional__grid", {
						"offer--group__grid": isGroup,
					})}
				>
					<OfferTips tipsToRender={tipsForRender} />
				</div>
			</div>
		);
	}
	return (
		<>
			<div
				className={classNames("offer--additional__header", {
					"offer--additional--grouped__header": isGroup,
				})}
			>
				<>
					<BettingTypeName
						name={name}
						isGroup={isGroup}
						bettingType={bettingType}
						offerKey={offerKey}
						bettingTypeStats={props.toggleState}
						setBettingTypeStats={props.setToggleState}
						index={props.index}
						isFull={props.index != 0}
					/>

					{props.index == 0 && (
						<div className="offer--additional__actions">
							<BettingTypeStatisticsButton
								toggleState={props.toggleState}
								setToggleState={props.setToggleState}
								//@ts-expect-error
								bettingType={bettingType}
								offerKey={offerKey}
							/>

							<Button
								className={
									"btn--square btn--square--tny btn--ghost offer--additional__btn" /* +
									(props.toggleState?.action == "info"
										? " btn--secondary"
										: " btn--ghost") */
								}
								iconOnly={true}
								iconClassName={
									"u-icon--sml" +
									(props.toggleState?.action == "info"
										? " u-icon--info-circle--bold"
										: " u-icon--info-circle")
								}
								onClick={() =>
									props.setToggleState("info", null)
								}
							/>
						</div>
					)}
				</>
			</div>

			{props.toggleState?.action === "stats" && props.index == 0 ? (
				<BettingTypeStatistics
					bettingTypeStats={props.toggleState}
					bettingTypeAbrv={bettingType.abrv}
				/>
			) : null}

			{props.index == 0 && (
				<BettingTypeDescription
					description={nameProvider.getName(
						bettingType.description,
						specifiers
					)}
					shouldDisplay={props.toggleState?.action == "info"}
				/>
			)}

			<div
				className={classNames(
					[
						`offer--additional__market offer--additional__market--${bettingType.abrv}`,
					],
					{
						"offer--group__market": isGroup,
					}
				)}
			>
				<div
					className={classNames("offer--additional__grid", {
						"offer--group__grid": isGroup,
						"offer--additional__grid--margin":
							TWO_COLUMNS_BETTING_TYPES.includes(
								bettingType.abrv
							),
					})}
				>
					<OfferTips tipsToRender={tipsForRender} />
				</div>
			</div>
		</>
	);
});
